import { useEffect, useRef, useState } from 'react'
import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion'

/**
 * A small soccer ball that follows the cursor with spring lag.
 * On any click it: (1) bounces up + rotates, (2) spawns 6 confetti balls that fly out.
 * Auto-disables on touch devices and when `prefers-reduced-motion: reduce`.
 * Remove this component from PageShell to fully disable.
 */

interface Burst {
  id: number
  x: number
  y: number
  angle: number
}

const BALL_SIZE = 26

export function FootballCursor() {
  const x = useMotionValue(-100)
  const y = useMotionValue(-100)
  const sx = useSpring(x, { stiffness: 220, damping: 22, mass: 0.5 })
  const sy = useSpring(y, { stiffness: 220, damping: 22, mass: 0.5 })
  const [bursts, setBursts] = useState<Burst[]>([])
  const [visible, setVisible] = useState(false)
  const [enabled, setEnabled] = useState(true)
  const [bouncing, setBouncing] = useState(false)
  const idRef = useRef(0)

  useEffect(() => {
    // Disable on touch / reduced motion
    const isTouch = matchMedia('(hover: none)').matches
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
    if (isTouch || reduced) {
      setEnabled(false)
      return
    }

    let lastPointerType: string = 'mouse'
    function handleMove(e: PointerEvent) {
      lastPointerType = e.pointerType
      if (e.pointerType !== 'mouse') return
      x.set(e.clientX - BALL_SIZE / 2)
      y.set(e.clientY - BALL_SIZE / 2)
      setVisible(true)
    }
    function handleLeave() {
      setVisible(false)
    }
    function handleClick(e: MouseEvent) {
      if (lastPointerType !== 'mouse') return
      // Don't bounce when clicking inside form inputs
      const t = e.target as HTMLElement
      if (t.closest('input, textarea, select, [contenteditable="true"]')) return

      setBouncing(true)
      setTimeout(() => setBouncing(false), 380)

      const cx = e.clientX
      const cy = e.clientY
      const newBursts: Burst[] = Array.from({ length: 6 }, (_, i) => ({
        id: idRef.current++,
        x: cx,
        y: cy,
        angle: (Math.PI * 2 * i) / 6 + Math.random() * 0.6,
      }))
      setBursts((prev) => [...prev, ...newBursts])
      setTimeout(() => {
        setBursts((prev) => prev.filter((b) => !newBursts.some((n) => n.id === b.id)))
      }, 900)
    }

    window.addEventListener('pointermove', handleMove, { passive: true })
    window.addEventListener('pointerleave', handleLeave)
    window.addEventListener('click', handleClick)
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerleave', handleLeave)
      window.removeEventListener('click', handleClick)
    }
  }, [x, y])

  if (!enabled) return null

  return (
    <>
      <motion.div
        className="pointer-events-none fixed top-0 left-0 z-[9999]"
        style={{ x: sx, y: sy, width: BALL_SIZE, height: BALL_SIZE, opacity: visible ? 1 : 0 }}
      >
        <motion.div
          animate={bouncing ? { scale: [1, 1.6, 0.85, 1], rotate: [0, 180, 320, 380] } : { rotate: 0 }}
          transition={bouncing ? { duration: 0.38, ease: 'easeOut' } : { duration: 0.2 }}
        >
          <SoccerBall size={BALL_SIZE} />
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {bursts.map((b) => {
          const dist = 60 + Math.random() * 30
          const dx = Math.cos(b.angle) * dist
          const dy = Math.sin(b.angle) * dist
          return (
            <motion.div
              key={b.id}
              className="pointer-events-none fixed top-0 left-0 z-[9998]"
              style={{ x: b.x - 7, y: b.y - 7 }}
              initial={{ opacity: 1, scale: 0.5 }}
              animate={{
                x: b.x - 7 + dx,
                y: b.y - 7 + dy + 30,
                rotate: 540,
                scale: 0.4,
                opacity: 0,
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            >
              <SoccerBall size={14} />
            </motion.div>
          )
        })}
      </AnimatePresence>
    </>
  )
}

/** Tiny inline SVG soccer ball — no network cost. */
function SoccerBall({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))' }}
      aria-hidden
    >
      <circle cx="12" cy="12" r="11" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="1.5" />
      <polygon points="12,7 15.5,9.4 14.2,13.4 9.8,13.4 8.5,9.4" fill="#0A0A0A" />
      <line x1="12" y1="7" x2="12" y2="2.5" stroke="#0A0A0A" strokeWidth="1.2" />
      <line x1="15.5" y1="9.4" x2="19" y2="8.5" stroke="#0A0A0A" strokeWidth="1.2" />
      <line x1="14.2" y1="13.4" x2="16.5" y2="17.5" stroke="#0A0A0A" strokeWidth="1.2" />
      <line x1="9.8" y1="13.4" x2="7.5" y2="17.5" stroke="#0A0A0A" strokeWidth="1.2" />
      <line x1="8.5" y1="9.4" x2="5" y2="8.5" stroke="#0A0A0A" strokeWidth="1.2" />
    </svg>
  )
}
